/** @format */

/* eslint @typescript-eslint/prefer-nullish-coalescing: "warn", @typescript-eslint/no-unsafe-enum-comparison: "warn" */
import { TFunction } from 'i18next';

import { ProductCategory } from '@toucan/client/global/application';
import { Market, defaultMarket } from '@toucan/client/global/market';
import {
    Department,
    Taxonomy,
    TaxonomyTerm,
    TaxonomyTermPage,
} from '@toucan/client/global/taxonomy';

import { PopoutMenuItem } from '@toucan/client/features/layout/components/header/components/popout-menu';

// Touch and Change
// eslint-disable-next-line @typescript-eslint/consistent-type-definitions
export interface PreparedCategoryUrl {
    url: string;
    slug: string;
}

/**
 * Returns a category URL prefixed with c, inserting that prefix after the
 * market prefix in the URL but before the path.
 *
 * @param {string} url The URL to transform
 * @return {PreparedCategoryUrl} The transformed URL and slug
 */
export function prepareCategoryUrl(
    url: string,
    t: TFunction
): PreparedCategoryUrl {
    const [market, ...categorySlugParts] = url
        .replace(/^\//, '')
        .replace(/\/$/, '')
        .split('/');

    const slug = categorySlugParts.join('/');
    const categorySlug = t('category', { ns: 'urls', lng: market });

    return {
        url: decodeURI(`/${market}/${categorySlug}/${slug}`),
        slug,
    };
}

/**
 * Returns a brand URL
 *
 * @param {string} url The URL to transform
 * @return {PreparedCategoryUrl} The transformed URL and slug
 */
export function prepareBrandUrl(url: string): PreparedCategoryUrl {
    const [market, ...categorySlugParts] = url
        .replace(/^\//, '')
        .replace(/\/$/, '')
        .split('/');

    const slug = categorySlugParts.join('/');

    return {
        url: decodeURI(`/${market}/${slug}`),
        slug,
    };
}

export function getAccountLinks(
    activeMarket: Market,
    t: TFunction
): PopoutMenuItem[] {
    const accountSlug = t('account', { ns: 'urls' });
    const marketSlug = activeMarket.slug;

    return [
        {
            text: t('My Account', { context: 'userMenu' }),
            url: `/${marketSlug}/${accountSlug}`,
            templatePath: '/account/',
        },
        {
            text: t('Profile and settings', { context: 'userMenu' }),
            url: `/${marketSlug}/${accountSlug}/${t('personal-details', {
                ns: 'urls',
            })}`,
            templatePath: '/account/personal-details',
        },
        {
            text: t('Quotes', { context: 'userMenu' }),
            url: `/${marketSlug}/${accountSlug}/${t('open-quotes', {
                ns: 'urls',
            })}`,
            templatePath: '/account/open-quotes',
        },
        {
            text: t('Transactions', { context: 'userMenu' }),
            url: `/${marketSlug}/${accountSlug}/${t('transactions', {
                ns: 'urls',
            })}`,
            templatePath: '/account/transactions',
        },
        {
            text: t('Saved', { context: 'userMenu' }),
            url: `/${marketSlug}/${accountSlug}/${t('saved-models', {
                ns: 'urls',
            })}`,
            templatePath: '/account/saved-models',
        },
    ];
}

export enum CategoryNavigationType {
    CATEGORY,
    BRAND,
}

/**
 * Walk the category tree and return navigation links from it
 *
 * @param {Taxonomy} tree - The taxonomy tree
 * @param {Market} activeMarket The current market
 * @param {Tfunction} t Translation function
 * @param {CategoryNavigationType} type The type of category navigation. one of:
 * 1. BRAND
 * 2. CATEGORY
 *
 * @return {ProductCategory}
 */
export function getTaxonomyNavigation(
    tree: Taxonomy | undefined,
    activeMarket: Market = defaultMarket,
    t: TFunction,
    type: CategoryNavigationType = CategoryNavigationType.CATEGORY
): ProductCategory[] | undefined {
    const language = activeMarket.slug;
    let templatePath: (slug: string) => string;
    let predicate: (page: TaxonomyTermPage) => boolean;

    switch (type) {
        case CategoryNavigationType.BRAND:
            templatePath = (slug: string) => `/brand?slug=${slug}`;
            predicate = (page: TaxonomyTermPage) =>
                page.marketUrlSlug === language;
            break;
        case CategoryNavigationType.CATEGORY:
            templatePath = (slug: string) => `/category?slug=${slug}`;
            predicate = (page: TaxonomyTermPage) =>
                page.marketUrlSlug === language &&
                page.department === Department.USED;
            break;
    }

    /**
     * Iterate over a category and recurse over all child categories
     * @param category
     */
    function iterate(category: TaxonomyTerm): ProductCategory {
        const categoryPage = category.categoryPage.find(predicate);

        return {
            id: category.id ?? null,
            title: categoryPage?.title || categoryPage?.name || '',
            url: categoryPage?.url || '',
            templatePath: templatePath(categoryPage?.slug || ''),
            categories: category.children.map(iterate),
        };
    }

    // Begin walking the tree
    return tree?.children?.map(iterate);
}
